import {
  CreateInventoryMeterOutputDto,
  InventoryMeterMountType,
  isOutputTechnology,
  SerializedInventoryMeterOutput,
} from '@enmon/commons/lib/tools';
import { DatePicker, Form, Input, Switch, UploadFile } from 'antd';
import { Divider } from 'components/Divider/Divider';
import { FormItem } from 'components/Form/FormItem';
import { ImageUpload } from 'components/ImageUpload/ImageUpload';
import { ManufacturerSelect } from 'components/Select/Manufacturer';
import { AddonOutputSelect } from 'components/Select/MeterAddonOutput';
import { MeterModelSelect } from 'components/Select/MeterModel';
import { Select } from 'components/Select/Select';
import { FORM_LAYOUT_CONFIG } from 'consts/common';
import { useMeterContext } from 'contexts/Meter';
import { useScrollTopOnMount } from 'hooks/useScrollTopOnMount';
import { Fragment, useEffect, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getMeterMountTechnologySelectOptions } from 'routes/inventorize_meter/steps/reading/utils/getMeterMountTechnologyOptions';
import { getMeterMountTypeSelectOptions } from 'routes/inventorize_meter/steps/utils/getMeterMountTypeOptions';
import { RegisterStates } from 'routes/meter_replacement/components/RegisterStates';
import { useReplaceMeterStepsStore } from 'routes/meter_replacement/useStepsStore';
import { selectMeterModel, useReplaceMeterStore } from 'routes/meter_replacement/useStore';
import { useMeterModelSuggestions } from '../../../../hooks/meter_models/useMeterModelDetails';
import { useMeterModels } from '../../../../hooks/meter_models/useMeterModels';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';
import { NEW_VALUES_FORM_ID } from './constants';
import { NewValuesStepContent as messages } from './messages';
import { NewValuesStepForm } from './slice';

export const NewValuesStepContent: VFC = () => {
  const [form] = Form.useForm();
  const { enmonMeter } = useMeterContext();
  const { commodity } = enmonMeter ?? {};
  const intl = useIntl();

  const { setNextStep } = useReplaceMeterStepsStore();
  const {
    newSerialNumber,
    newRegisterStates,
    calibrated,
    manufactured,
    manufacturer,
    meterModel,
    setNewValuesStepValues,
    outputs,
    protocolPhotos,
    detailPhotos,
    meterMountType,
    meterMountTechnology,
    commodity: storeCommodity,
  } = useReplaceMeterStore();
  const { meterModels } = useMeterModels(commodity);
  const { setMeterModelSuggestions } = useMeterModelSuggestionsStore();
  const { meterModel: selectedMeterModel } = useReplaceMeterStore(selectMeterModel(meterModels));

  const meterModelSuggestions = useMeterModelSuggestions(selectedMeterModel);
  const { inbuilt_outputs: inbuiltOutputs } = meterModelSuggestions ?? {};

  useEffect(() => {
    const outputOptions = (inbuiltOutputs?.selection ?? (inbuiltOutputs?.value ? [inbuiltOutputs.value] : [])).filter(
      isOutputTechnology,
    );

    // Update form/store only when there is a difference between current outputs array and suggested outputs
    if (
      outputOptions.length !== outputs?.length ||
      !outputOptions.every(option => outputs?.some(output => output.technology === option))
    ) {
      const newOutputs = outputOptions.map(
        technology => ({ technology, is_occupied: false }) satisfies CreateInventoryMeterOutputDto,
      );

      form.setFieldsValue({
        outputs: newOutputs,
      });
      setNewValuesStepValues({ outputs: newOutputs });
    }

    if (storeCommodity !== commodity) {
      setNewValuesStepValues({ commodity });
    }
  }, [inbuiltOutputs, form, outputs, setNewValuesStepValues, commodity, storeCommodity]);

  const handleValuesChange = (changedValues: Partial<NewValuesStepForm>, allValues: NewValuesStepForm) => {
    if ('newRegisterStates' in changedValues) {
      setNewValuesStepValues({ newRegisterStates: allValues.newRegisterStates });
    } else if ('outputs' in changedValues) {
      allValues.outputs?.forEach(output => {
        if (output.is_occupied === false) output.remote_reading_owner = undefined;
      });
      setNewValuesStepValues({ outputs: allValues.outputs });
    } else {
      setNewValuesStepValues(allValues);
    }

    if ('manufacturer' in changedValues) {
      form.setFieldsValue({ meterModel: undefined });
      setNewValuesStepValues({ meterModel: undefined });
    }
  };

  const handleSubmit = () => {
    setMeterModelSuggestions(meterModelSuggestions);
    setNextStep();
  };

  const handlePhotoSelect = (fieldName: number, fileList: UploadFile[]) => {
    const updatedRegisterStates = newRegisterStates?.map((registerState, index) => {
      if (index === fieldName) {
        return { ...registerState, photos: fileList };
      }
      return registerState;
    });
    form.setFieldsValue({ newRegisterStates: updatedRegisterStates });
    setNewValuesStepValues({ newRegisterStates: updatedRegisterStates });
  };

  const handleProtocolDetailPhotoSelect = (photoType: 'protocolPhotos' | 'detailPhotos', fileList: UploadFile[]) => {
    const updates = { [photoType]: fileList };
    form.setFieldsValue(updates);
    setNewValuesStepValues(updates);
  };

  useScrollTopOnMount();

  return (
    <Form
      form={form}
      id={NEW_VALUES_FORM_ID}
      layout="horizontal"
      onFinish={handleSubmit}
      onValuesChange={handleValuesChange}
      {...FORM_LAYOUT_CONFIG}
    >
      <FormItem
        required
        initialValue={newSerialNumber}
        label={<FormattedMessage {...messages.newSerialNumberLabel} />}
        name={'newSerialNumber' satisfies keyof NewValuesStepForm}
        rules={[{ required: true, message: intl.formatMessage(messages.newSerialNumberRequired) }]}
      >
        <Input placeholder="1514251" />
      </FormItem>
      <FormItem
        initialValue={manufacturer}
        label={<FormattedMessage {...messages.manufacturer} />}
        name={'manufacturer' satisfies keyof NewValuesStepForm}
      >
        <ManufacturerSelect commodity={commodity ?? undefined} />
      </FormItem>
      <FormItem
        initialValue={meterModel}
        label={<FormattedMessage {...messages.meterModelLabel} />}
        name={'meterModel' satisfies keyof NewValuesStepForm}
      >
        <MeterModelSelect commodity={commodity ?? undefined} manufacturer={manufacturer} />
      </FormItem>
      <FormItem
        initialValue={manufactured}
        label={<FormattedMessage {...messages.manufacturedLabel} />}
        name={'manufactured' satisfies keyof NewValuesStepForm}
      >
        <DatePicker inputReadOnly className="w-100" picker="year" placeholder="2022" />
      </FormItem>
      <FormItem
        initialValue={calibrated}
        label={<FormattedMessage {...messages.calibratedLabel} />}
        name={'calibrated' satisfies keyof NewValuesStepForm}
      >
        <DatePicker inputReadOnly className="w-100" picker="year" placeholder="2024" />
      </FormItem>
      {(outputs ?? []).length > 0 && (
        <>
          <Divider>
            <FormattedMessage {...messages.inbuiltOutputs} />
          </Divider>

          <Form.List initialValue={outputs} name="outputs">
            {fields =>
              fields.map((field, index) => (
                <Fragment key={field.key}>
                  <FormItem
                    label={`${intl.formatMessage(messages.technology)} ${index + 1}`}
                    name={[field.name, 'technology' satisfies keyof SerializedInventoryMeterOutput]}
                  >
                    <AddonOutputSelect disabled />
                  </FormItem>
                  {outputs?.[field.name]?.is_occupied === true && (
                    <FormItem
                      label={<FormattedMessage {...messages.remoteReadingOwner} />}
                      name={[field.name, 'remote_reading_owner' satisfies keyof SerializedInventoryMeterOutput]}
                    >
                      <Input placeholder={`${intl.formatMessage(messages.remoteReadingOwner)} ${index + 1}`} />
                    </FormItem>
                  )}
                  <FormItem
                    label={<FormattedMessage {...messages.isOccupied} />}
                    name={[field.name, 'is_occupied' satisfies keyof SerializedInventoryMeterOutput]}
                    valuePropName="checked"
                  >
                    <Switch />
                  </FormItem>
                </Fragment>
              ))
            }
          </Form.List>
        </>
      )}

      <Divider>
        <FormattedMessage {...messages.registers} />
      </Divider>
      <RegisterStates
        fieldName={'newRegisterStates' satisfies keyof NewValuesStepForm}
        registerStates={newRegisterStates ?? []}
        onPhotoSelect={handlePhotoSelect}
      />

      <Divider>
        <FormattedMessage {...messages.meterMountTechnology} />
      </Divider>
      <FormItem
        required
        initialValue={meterMountType}
        label={<FormattedMessage {...messages.installationStatus} />}
        name="meterMountType"
        rules={[{ required: true, message: intl.formatMessage(messages.installationStatusRequired) }]}
        tooltip={<FormattedMessage {...messages.installationStatusTooltip} />}
      >
        <Select
          className="w-100"
          options={getMeterMountTypeSelectOptions(commodity, intl)}
          placeholder={<FormattedMessage {...messages.meterMountTechnologyPlaceholder} />}
        />
      </FormItem>
      {meterMountType && meterMountType !== InventoryMeterMountType.Free && (
        <FormItem
          initialValue={meterMountTechnology}
          label={<FormattedMessage {...messages.meterMountTechnologyLabel} />}
          name="meterMountTechnology"
          tooltip={<FormattedMessage {...messages.meterMountTechnologyTooltip} />}
        >
          <Select
            allowClear
            className="w-100"
            options={getMeterMountTechnologySelectOptions(commodity, intl)}
            placeholder={<FormattedMessage {...messages.meterMountTechnologyPlaceholder} />}
          />
        </FormItem>
      )}
      <FormItem
        required
        initialValue={protocolPhotos}
        label={<FormattedMessage {...messages.protocolPhotosLabel} />}
        name={'protocolPhotos' satisfies keyof NewValuesStepForm}
        rules={[
          {
            required: true,
            message: intl.formatMessage(messages.protocolPhotosRequired),
          },
        ]}
      >
        <ImageUpload
          multiple
          fileList={protocolPhotos}
          name="protocolPhotos"
          onImageUpload={fileList => handleProtocolDetailPhotoSelect('protocolPhotos', fileList)}
        />
      </FormItem>

      <FormItem
        required
        help={<FormattedMessage {...messages.detailPhotosHelp} />}
        initialValue={detailPhotos}
        label={<FormattedMessage {...messages.detailPhotosLabel} />}
        name={'detailPhotos' satisfies keyof NewValuesStepForm}
        rules={[
          {
            required: true,
            message: intl.formatMessage(messages.detailPhotosRequired),
          },
        ]}
        tooltip={<FormattedMessage {...messages.detailPhotosTooltip} />}
      >
        <ImageUpload
          multiple
          fileList={detailPhotos}
          name="detailPhotos"
          onImageUpload={fileList => handleProtocolDetailPhotoSelect('detailPhotos', fileList)}
        />
      </FormItem>
    </Form>
  );
};
